import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Drawer from '@material-ui/core/Drawer';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import {
  isAuthenticated,
  sendLogin,
  changeEmalAddress,
  verifyEmailSubmit
} from '../../auth/Auth';
import {
  getAccounts,
  editAccountStatus,
  editAccountEmail
} from '../../actions/Accounts';
import { getShopDetail, editShop } from '../../actions/Shop';
import { getGroupDetail, editGroup } from '../../actions/Groups';
import ValidationText from '../../components/atoms/ValidationText';
import RequiredButton from '../../components/atoms/RequiredButton';
import ShopChangeButton from '../../components/atoms/ShopChangeButton';
import ShopSendCodeButton from '../../components/atoms/ShopSendCodeButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import MenuBox from '../../components/MenuBox';

const styles = theme => ({
  root: {
    backgroundColor: '#F7F7F7',
    flexGrow: 1,
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 375,
      margin: '0 auto'
    }
  },
  pageTitleDiv: {
    paddingLeft: 15,
    paddingTop: 20,
    paddingBottom: 10,
    textAlign: 'left',
    backgroundColor: '#F7F7F7'
  },
  sendCodeBtnDiv: {
    textAlign: 'right',
    marginBottom: 30
  },
  textTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: '28px'
  },
  mainPaper: {
    position: 'relative',
    margin: '15px 15px 40px 15px'
  },
  searchForm: {
    padding: 20
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  textField: {
    padding: '0px 5px 0px 5px'
  },
  button: {
    margin: '5px 5px'
  },
  horizotalLine: {
    borderBottom: '1px solid #dddddd'
  }
});

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account
    }),
    dispatch => ({
      actions: bindActionCreators(
        {
          getAccounts,
          editAccountStatus,
          getShopDetail,
          editShop,
          getGroupDetail,
          editGroup,
          editAccountEmail
        },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('isChangeSuccess', 'updateIsChangeSuccess', false),
  withState('resultMessage', 'setResultMessage', ''),
  withState('redirectPath', 'setRedirectPath', '/shops_shop'),
  withState('oldPassword', 'setOldPassword', ''),
  withState('password', 'setPassword', ''),
  withState('passwordError', 'setPasswordError', ''),
  withState('confirmPassword', 'setConfirmPassword', ''),
  withState('confirmPasswordError', 'setConfirmPasswordError', ''),
  withState('phone', 'setPhone', ''),
  withState('phoneAuthCode', 'setPhoneAuthCode', ''),
  withState('email', 'setEmail', ''),
  withState('emailError', 'setEmailError', ''),
  withState('emailAuthCode', 'setEmailAuthCode', ''),
  withState('emailAuthCodeError', 'setEmailAuthCodeError', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(data => {
        this.props.checkSession(data);
      });
    }
  })
);

const Main = enhance(
  ({
    classes,
    actions,
    checkSession,
    handleRedirect,
    account,
    openDrawer,
    updateOpenDrawer,
    openProgress,
    updateOpenProgress,
    loading,
    updateLoading,
    isChangeSuccess,
    updateIsChangeSuccess,
    resultMessage,
    setResultMessage,
    redirectPath,
    setRedirectPath,
    email,
    setEmail,
    emailAuthCode,
    setEmailAuthCode,
    emailError,
    setEmailError,
    emailAuthCodeError,
    setEmailAuthCodeError
  }) => {
    const isShop = typeof account !== 'undefined' && account.role === 'SHOP';
    const isReleased =
      account && account.accountStatus && account.accountStatus.status === true;

    // check if is account shop
    if (!(isShop && isReleased)) {
      handleRedirect('/shops');
    }

    const errors = {
      emailError,
      emailAuthCodeError
    };
    const validEmail = () => {
      const value = email;
      if (value === '') {
        setEmailError('必須項目です。');
        return 1;
      }
      setEmailError('');
      setEmail(value);
      return 0;
    };
    const validEmailAuthCode = () => {
      const value = emailAuthCode;
      if (value === '') {
        setEmailAuthCodeError('必須項目です。');
        return 1;
      }
      setEmailAuthCodeError('');
      setEmailAuthCode(value);
      return 0;
    };
    const valid = () => {
      const errorCount = validEmail() + validEmailAuthCode();
      if (errorCount === 0) {
        return true;
      }
      return false;
    };

    const sendCodeAction = () => {
      if (validEmail() !== 0) {
        alert('メールアドレスを正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenProgress(true);
        changeEmalAddress(email)
          .then(async () => {
            let result = false;
            if (account.role === 'SHOP') {
              const detailData = await actions.getShopDetail(account.shopId);
              detailData.payload.email = email;
              const editShopResult = await actions
                .editShop(account.shopId, detailData.payload)
                .then(data => checkSession(data));
              const editEmailResult = await actions.editAccountEmail(
                account.manageAccountId,
                {
                  shopId: account.shopId,
                  email
                }
              );
              if (
                editShopResult.type === 'EDIT_SHOP_SUCCESS' &&
                editEmailResult.type === 'EDIT_ACCOUNT_EMAIL_SUCCESS'
              ) {
                result = true;
              }
            } else if (account.role === 'GROUP') {
              const detailData = await actions.getGroupDetail(account.groupId);
              detailData.payload.email = email;
              const editGroupResult = await actions
                .editGroup(account.groupId, detailData.payload)
                .then(data => checkSession(data));
              const editEmailResult = await actions.editAccountEmail(
                account.manageAccountId,
                {
                  groupId: account.groupId,
                  email
                }
              );
              if (
                editGroupResult.type === 'EDIT_GROUP_SUCCESS' &&
                editEmailResult.type === 'EDIT_ACCOUNT_EMAIL_SUCCESS'
              ) {
                result = true;
              }
            } else {
              const editEmailResult = await actions.editAccountEmail(
                account.manageAccountId,
                {
                  email
                }
              );
              if (editEmailResult.type === 'EDIT_ACCOUNT_EMAIL_SUCCESS') {
                result = true;
              }
            }

            if (result) {
              setResultMessage(
                '認証コードを送信しました。メールをご確認ください。'
              );
              updateLoading(false);
            } else {
              setResultMessage('認証コード送信に失敗しました。');
              updateLoading(false);
            }
          })
          .catch(() => {
            updateIsChangeSuccess(false);
            setResultMessage('認証コード送信に失敗しました。');
            updateLoading(false);
          });
      }
    };
    const changeAction = async () => {
      if (!valid()) {
        alert('登録情報を正しく入力してください。');
      } else {
        updateLoading(true);
        updateOpenProgress(true);
        await actions
          .getAccounts({ cognitoUserId: '' })
          .then(data => checkSession(data));
        if (account.role === 'GROUP') {
          setRedirectPath('/groups_group');
        } else if (isReleased !== null && !isReleased) {
          setRedirectPath('/release_step');
        }
        await verifyEmailSubmit(emailAuthCode)
          .then(async () => {
            updateIsChangeSuccess(true);
            setResultMessage('メールアドレスを変更しました。');
            updateLoading(false);
          })
          .catch(() => {
            updateIsChangeSuccess(false);
            setResultMessage('メールアドレス変更に失敗しました。');
            updateLoading(false);
          });
      }
    };

    const progressOkAction = () => {
      if (isChangeSuccess) {
        updateOpenProgress(false);
        handleRedirect(redirectPath);
      } else {
        updateOpenProgress(false);
      }
    };

    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitleDiv}>
          <Typography className={classes.textTitle}>
            メールアドレス変更
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <div>
              <RequiredButton />
            </div>
            <TextField
              required
              label="メールアドレス"
              placeholder="gochimeshi@gochi.com"
              type="email"
              fullWidth
              className={classes.textField}
              margin="normal"
              onChange={e => {
                setEmail(e.target.value);
              }}
              onBlur={validEmail}
            />
            <ValidationText message={errors.emailError} />
            <div className={classes.sendCodeBtnDiv}>
              <ShopSendCodeButton onClickAction={sendCodeAction} />
            </div>
            <div>
              <RequiredButton />
            </div>
            <TextField
              required
              label="認証コード"
              fullWidth
              className={classes.textField}
              margin="normal"
              onChange={e => {
                setEmailAuthCode(e.target.value);
              }}
              onBlur={validEmailAuthCode}
            />
            <ValidationText message={errors.emailAuthCodeError} />
          </div>
          <div className={classes.horizotalLine} />
          <div className={classes.searchForm}>
            <ShopChangeButton onClickAction={changeAction} />
          </div>
          <ProgressDialog
            openProgress={openProgress}
            progressOkAction={progressOkAction}
            loading={loading}
            resultMessage={resultMessage}
          />
        </Paper>
        <MenuBox account={account} />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
